/**
 * portalToken.js
 *
 * Generates a short-lived HMAC-SHA256-signed JWT that the backend uses to
 * verify WHICH portal originated the request.
 *
 * How it works:
 *  - Each portal has its own secret stored in a VITE_ / REACT_APP_ env var.
 *  - At login time, the frontend signs a mini-JWT containing {portal, role}.
 *  - The backend holds the same secrets and verifies the signature.
 *  - Because only the backend (and the build environment) knows the secret,
 *    a browser user cannot forge a different role.
 *
 * NOTE: This is a pure-JS implementation that works in any modern browser/Node env
 * without needing the `jsonwebtoken` npm package on the frontend.
 */

function base64UrlEncode(str) {
    return btoa(str)
        .replace(/\+/g, '-')
        .replace(/\//g, '_')
        .replace(/=/g, '');
}

async function hmacSign(secret, message) {
    const enc = new TextEncoder();
    const key = await crypto.subtle.importKey(
        'raw',
        enc.encode(secret),
        { name: 'HMAC', hash: 'SHA-256' },
        false,
        ['sign']
    );
    const sig = await crypto.subtle.sign('HMAC', key, enc.encode(message));
    // Convert ArrayBuffer to base64url
    return base64UrlEncode(String.fromCharCode(...new Uint8Array(sig)));
}

/**
 * Creates a signed portal JWT.
 *
 * @param {string} portal  - 'admin' | 'merchant' | 'distributor'
 * @param {string} role    - 'ADMIN' | 'MERCHANT' | 'DISTRIBUTOR'
 * @param {string} secret  - HMAC secret from env (e.g. import.meta.env.VITE_PORTAL_SECRET)
 * @param {number} ttlSecs - Token lifetime in seconds (default 5 minutes)
 * @returns {Promise<string>} - A compact JWT string
 */
export async function generatePortalToken(portal, role, secret, ttlSecs = 300) {
    const header = base64UrlEncode(JSON.stringify({ alg: 'HS256', typ: 'JWT' }));
    const now = Math.floor(Date.now() / 1000);
    const payload = base64UrlEncode(JSON.stringify({
        portal,
        role,
        iat: now,
        exp: now + ttlSecs,
    }));

    const signature = await hmacSign(secret, `${header}.${payload}`);
    return `${header}.${payload}.${signature}`;
}
